import { z } from "zod";
import { Timestamps } from "../common/timestamps.schema.ts";
import { CustomerId } from "../fsm/fsm.schema.ts";

export const LocationId = z.string().uuid().brand<"LocationId">();
export type LocationId = z.infer<typeof LocationId>;

export const LocationType = z.enum(["client", "depot"]);
export type LocationType = z.infer<typeof LocationType>;

export const CoordinatesSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});
export type Coordinates = z.infer<typeof CoordinatesSchema>;

export const LocationDto = z
  .object({
    id: LocationId,
    customerId: CustomerId,
    name: z.string(),
    city: z.string(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
    coordinates: CoordinatesSchema,
    locationType: LocationType,
    repairCode: z.string().nullable(),
    key: z.string().max(3).nullable(),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);
export type LocationDto = z.infer<typeof LocationDto>;

// Minimal-fields DTO for request-builder pickers (gated by requests.write, not admin.locations.read)
export const LocationPickerDto = z.object({
  id: LocationId,
  customerId: CustomerId,
  name: z.string(),
  city: z.string(),
});
export type LocationPickerDto = z.infer<typeof LocationPickerDto>;

export const CreateLocationInput = z.object({
  customerId: CustomerId,
  name: z.string().min(1),
  city: z.string().min(1),
  address: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  coordinates: CoordinatesSchema.optional(),
  locationType: LocationType.optional(),
});
export type CreateLocationInput = z.infer<typeof CreateLocationInput>;

export const UpdateLocationInput = z.object({
  name: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  address: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  coordinates: CoordinatesSchema.optional(),
  locationType: LocationType.optional(),
  key: z.string().max(3).nullable().optional(),
});
export type UpdateLocationInput = z.infer<typeof UpdateLocationInput>;

export const LocationBudgetPeriodId = z
  .string()
  .uuid()
  .brand<"LocationBudgetPeriodId">();
export type LocationBudgetPeriodId = z.infer<typeof LocationBudgetPeriodId>;

export const LocationBudgetPeriodDto = z.object({
  id: LocationBudgetPeriodId,
  locationId: z.string().uuid(),
  amount: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  supersededBy: LocationBudgetPeriodId.nullable(),
});
export type LocationBudgetPeriodDto = z.infer<typeof LocationBudgetPeriodDto>;

export const LocationBudgetSummaryDto = z.object({
  locationId: LocationId,
  locationName: z.string(),
  budgetTotal: z.string().nullable(),
  spent: z.string(),
  openOrders: z.string(),
  available: z.string().nullable(),
});
export type LocationBudgetSummaryDto = z.infer<typeof LocationBudgetSummaryDto>;

export const CreateLocationBudgetPeriodInput = z
  .object({
    amount: z.string().refine((v) => parseFloat(v) >= 0, {
      message: "Amount cannot be negative",
    }),
    effectiveFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
    effectiveTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  })
  .refine((v) => v.effectiveFrom <= v.effectiveTo, {
    message: "effectiveFrom must not be after effectiveTo",
    path: ["effectiveTo"],
  });
export type CreateLocationBudgetPeriodInput = z.infer<
  typeof CreateLocationBudgetPeriodInput
>;
